/**
 * Texto de uma mensagem de atendimento — fonte ÚNICA para as três telas.
 *
 * **O defeito que motivou (06/08/2026).** Ao medir o portal no navegador,
 * apareceu uma pílula `[anexo]` acima da saudação do bot, em toda sessão nova.
 * Causa: a mensagem do bot é `{type: "bot_question", prompt: "Olá! …"}` — o
 * texto está em `prompt`, não em `text`, e nenhum dos renderizadores conhecia
 * esse formato. No portal caía no `else` e virava `[anexo]`; em `/atendimentos`
 * caía em `JSON.stringify` e a tela mostrava o **JSON cru** — a mesma queixa que
 * o Augusto já havia relatado uma vez.
 *
 * Um defeito, duas aparências, porque a extração de texto estava duplicada em
 * três arquivos. É a mesma classe de "listas à mão divergem" do
 * `MessageBubble.tipos.test.tsx` — só que naquele o eixo era o TIPO DE ANEXO, e
 * aqui é o TEXTO. O guarda de lá não podia ver este.
 *
 * **`JSON.stringify` não é fallback.** Mostrar JSON cru para um usuário nunca é
 * a resposta certa; para um parceiro externo é pior ainda, porque expõe nomes
 * de campos internos. Content desconhecido vira um rótulo neutro.
 */

/** Rótulo para content que nenhuma regra reconheceu. */
export const ROTULO_DESCONHECIDO = "[mensagem]";

function tamanhoEmKb(content: Record<string, unknown>): string {
  const size = typeof content?.size === "number" ? content.size : 0;
  return size > 0 ? ` · ${Math.ceil(size / 1024)} KB` : "";
}

/**
 * O texto a exibir para um `content` de mensagem.
 *
 * A ordem importa: `text` primeiro porque é o caso comum, e `prompt` logo
 * depois porque as mensagens do bot são as únicas que o usam.
 */
export function textoDaMensagem(content: Record<string, unknown> | null | undefined): string {
  if (!content) return ROTULO_DESCONHECIDO;

  if (typeof content.text === "string" && content.text) return content.text;

  // Bot de triagem: o enunciado da pergunta vive em `prompt`. As opções não
  // entram aqui — o `BotTriagePanel` as renderiza como botões clicáveis, e
  // repeti-las no histórico seria ruído.
  if (typeof content.prompt === "string" && content.prompt) return content.prompt;

  const type = typeof content.type === "string" ? content.type : "";
  const filename = typeof content.filename === "string" ? content.filename : "anexo";

  // Áudio SEM url é o fail-closed da A1: a re-assinatura da URL não aconteceu
  // (expirou, ou o pedido não tinha direito a ela). Dizer só "🎤 Áudio" faria
  // parecer que está tudo bem com um áudio que não vai tocar. A distinção
  // existia no `SessionChat` e quase se perdeu ao unificar o extrator — o teste
  // dele foi quem pegou.
  if (type === "audio") {
    return typeof content.url === "string" && content.url ? "🎤 Áudio" : "🎤 Áudio indisponível";
  }
  if (type === "image") return `🖼️ ${filename}${tamanhoEmKb(content)}`;
  if (type === "video") return `🎬 ${filename}${tamanhoEmKb(content)}`;
  if (type === "file") return `📎 ${filename}${tamanhoEmKb(content)}`;

  return ROTULO_DESCONHECIDO;
}
